import * as THREE from 'three';

/** Toolbar / WorldCommands primitive types supported by MCP `action_actor.create`. */
export const MCP_PRIMITIVE_TYPES = [
  'cube',
  'cone',
  'cylinder',
  'sphere',
  'torus',
  'capsule',
] as const;

export type McpPrimitiveType = (typeof MCP_PRIMITIVE_TYPES)[number];

export const MCP_PRIMITIVE_TYPE_SET = new Set<string>(MCP_PRIMITIVE_TYPES);

export const GEOMETRY_TYPES = {
  'Box Geometry': THREE.BoxGeometry,
  'Sphere Geometry': THREE.SphereGeometry,
  'Cylinder Geometry': THREE.CylinderGeometry,
  'Cone Geometry': THREE.ConeGeometry,
  'Plane Geometry': THREE.PlaneGeometry,
  'Circle Geometry': THREE.CircleGeometry,
  'Ring Geometry': THREE.RingGeometry,
  'Torus Geometry': THREE.TorusGeometry,
  'Torus Knot Geometry': THREE.TorusKnotGeometry,
  'Capsule Geometry': THREE.CapsuleGeometry,
  'Dodecahedron Geometry': THREE.DodecahedronGeometry,
  'Icosahedron Geometry': THREE.IcosahedronGeometry,
  'Octahedron Geometry': THREE.OctahedronGeometry,
  'Tetrahedron Geometry': THREE.TetrahedronGeometry,
} as const;

export type GeometryTypeLabel = keyof typeof GEOMETRY_TYPES;

/** Default constructor parameters for each geometry class name (sans spaces). */
export const GEOMETRY_DEFAULT_PARAMS: Record<string, Array<number | boolean>> = {
  BoxGeometry: [1, 1, 1, 1, 1, 1],
  SphereGeometry: [1, 32, 16, 0, Math.PI * 2, 0, Math.PI],
  CylinderGeometry: [1, 1, 1, 32, 1, false, 0, Math.PI * 2],
  ConeGeometry: [1, 1, 32, 1, false, 0, Math.PI * 2],
  PlaneGeometry: [1, 1, 1, 1],
  CircleGeometry: [1, 32, 0, Math.PI * 2],
  RingGeometry: [0.5, 1, 32, 1, 0, Math.PI * 2],
  TorusGeometry: [1, 0.4, 16, 32, Math.PI * 2],
  TorusKnotGeometry: [1, 0.4, 64, 8, 2, 3],
  CapsuleGeometry: [1, 1, 4, 8],
  DodecahedronGeometry: [1, 0],
  IcosahedronGeometry: [1, 0],
  OctahedronGeometry: [1, 0],
  TetrahedronGeometry: [1, 0],
};

export const getGeometryType = (geometry: THREE.BufferGeometry | null | undefined): GeometryTypeLabel | '' => {
  if (!geometry) return '';
  return Object.entries(GEOMETRY_TYPES).find(([, GeometryClass]) => geometry instanceof GeometryClass)?.[0] as GeometryTypeLabel | undefined ?? '';
};

export const getGeometryPropertyNames = (geometry: THREE.BufferGeometry): string[] => {
  if (geometry instanceof THREE.BoxGeometry) {
    return ['width', 'height', 'depth', 'widthSegments', 'heightSegments', 'depthSegments'];
  }
  if (geometry instanceof THREE.SphereGeometry) {
    return ['radius', 'widthSegments', 'heightSegments', 'phiStart', 'phiLength', 'thetaStart', 'thetaLength'];
  }
  if (geometry instanceof THREE.CylinderGeometry) {
    return ['radiusTop', 'radiusBottom', 'height', 'radialSegments', 'heightSegments', 'openEnded', 'thetaStart', 'thetaLength'];
  }
  if (geometry instanceof THREE.ConeGeometry) {
    return ['radius', 'height', 'radialSegments', 'heightSegments', 'openEnded', 'thetaStart', 'thetaLength'];
  }
  if (geometry instanceof THREE.PlaneGeometry) {
    return ['width', 'height', 'widthSegments', 'heightSegments'];
  }
  if (geometry instanceof THREE.CircleGeometry) {
    return ['radius', 'segments', 'thetaStart', 'thetaLength'];
  }
  if (geometry instanceof THREE.RingGeometry) {
    return ['innerRadius', 'outerRadius', 'thetaSegments', 'phiSegments', 'thetaStart', 'thetaLength'];
  }
  if (geometry instanceof THREE.TorusGeometry) {
    return ['radius', 'tube', 'radialSegments', 'tubularSegments', 'arc'];
  }
  if (geometry instanceof THREE.TorusKnotGeometry) {
    return ['radius', 'tube', 'tubularSegments', 'radialSegments', 'p', 'q'];
  }
  if (geometry instanceof THREE.CapsuleGeometry) {
    return ['radius', 'height', 'capSegments', 'radialSegments'];
  }
  if (
    geometry instanceof THREE.DodecahedronGeometry
    || geometry instanceof THREE.IcosahedronGeometry
    || geometry instanceof THREE.OctahedronGeometry
    || geometry instanceof THREE.TetrahedronGeometry
  ) {
    return ['radius', 'detail'];
  }
  return [];
};

export type GeometryCatalogEntry = {
  label: GeometryTypeLabel;
  className: string;
  defaultParams: Array<number | boolean>;
  parameterNames: string[];
};

export type GeometryCatalogSnapshot = {
  primitiveTypes: readonly McpPrimitiveType[];
  primitiveTypeHint: string;
  geometryTypes: GeometryCatalogEntry[];
};

/** Serializable geometry reference for MCP resources and agent discovery. */
export const getGeometryCatalogSnapshot = (): GeometryCatalogSnapshot => {
  const geometryTypes: GeometryCatalogEntry[] = [];

  for (const label of Object.keys(GEOMETRY_TYPES) as GeometryTypeLabel[]) {
    const className = label.replace(' ', '');
    const GeometryClass = GEOMETRY_TYPES[label];
    const defaultParams = GEOMETRY_DEFAULT_PARAMS[className] ?? [1, 1, 1];
    let parameterNames: string[] = [];
    try {
      const sampleGeometry = new GeometryClass(...(defaultParams as any[]));
      parameterNames = getGeometryPropertyNames(sampleGeometry);
      sampleGeometry.dispose();
    } catch {
      parameterNames = [];
    }
    geometryTypes.push({
      label,
      className,
      defaultParams,
      parameterNames,
    });
  }

  return {
    primitiveTypes: MCP_PRIMITIVE_TYPES,
    primitiveTypeHint: 'Use action_actor(action="create", primitiveType="sphere"|"cube"|...) for basic mesh primitives. These map to ENGINE.MeshComponent actors created via WorldCommands.placePrimitives.',
    geometryTypes,
  };
};
